import React, { useMemo, useContext, useEffect, useRef, useState } from "react";
import { NavLink, matchPath, useLocation } from "react-router-dom";
import Popover from "@mui/material/Popover";
import { useEnglishText } from "../../text/useEnglishText.js";
import {
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Headset,
  KeyRound,
  LayoutDashboard,
  LogIn,
  LogOut,
  MonitorCog,
  Network,
  ReceiptText,
  ScrollText,
  Settings2,
  ShieldCheck,
  UserRound,
  UsersRound,
  WalletCards,
} from "lucide-react";

import { AuthContext } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  Sidebar as SidebarSurface,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "../ui/sidebar";

import "../../styles/Sidebar.css";

const getActiveGroup = (items, pathname) => items.find((group) =>
  group.children?.some((item) => matchPath({ path: item.to, end: false }, pathname))
)?.id ?? null;

const InstantSidebarTooltip = ({ label, disabled, children }) => {
  if (disabled) return children;

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className="sidebar-tooltip" side="right" sideOffset={10}>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Sidebar = ({
  isLocked,
  setIsLocked,
  offcanvasOpen,
  setOffcanvasOpen,
  isMobile,
}) => {
  const { t } = useEnglishText();
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [flyout, setFlyout] = useState(null);

  const isExpanded = isLocked || (isMobile && offcanvasOpen);

  const toggleSidebar = () => {
    if (isMobile) {
      setOffcanvasOpen((open) => !open);
      return;
    }

    setIsLocked((locked) => !locked);
  };

  const handleAsideClick = (event) => {
    if (event.target.closest("a, button, [role='button'], input, select, textarea")) return;
    toggleSidebar();
  };

  const handleNavigation = () => {
    setFlyout(null);
    if (isMobile) setOffcanvasOpen(false);
  };

  const menuItems = useMemo(() => {
    if (!user) return [];

    return [
      {
        to: "/dashboard",
        icon: <LayoutDashboard />,
        label: t("sidebar.dashboard"),
        end: true,
      },

      {
        id: "organization",
        icon: <Network />,
        label: t("sidebar.organization"),
        children: [
          { to: "/dashboard/companies", icon: <Building2 />, label: t("sidebar.companies") },
          { to: "/dashboard/users", icon: <UsersRound />, label: t("sidebar.users") },
        ],
      },
      {
        id: "licensing",
        icon: <ShieldCheck />,
        label: t("sidebar.licensing"),
        children: [
          { to: "/dashboard/licenses", icon: <KeyRound />, label: t("sidebar.licenses") },
          { to: "/dashboard/license-requests", icon: <ClipboardCheck />, label: t("sidebar.licenseRequests") },
        ],
      },
      {
        id: "finance",
        icon: <CircleDollarSign />,
        label: t("sidebar.finance"),
        children: [
          { to: "/dashboard/wallet", icon: <WalletCards />, label: t("sidebar.wallet") },
          { to: "/dashboard/billing", icon: <ReceiptText />, label: t("sidebar.billing") },
        ],
      },
      {
        to: "/dashboard/tickets",
        icon: <Headset />,
        label: t("sidebar.tickets"),
      },
      {
        id: "administration",
        icon: <MonitorCog />,
        label: t("sidebar.administration"),
        children: [
          { to: "/dashboard/audit-logs", icon: <ScrollText />, label: t("sidebar.auditLogs") },
          { to: "/dashboard/settings", icon: <Settings2 />, label: t("sidebar.settings") },
        ],
      },
    ];
  }, [t, user]);

  const activeGroup = getActiveGroup(menuItems, location.pathname);
  const flyoutGroup = flyout
    ? menuItems.find((item) => item.id === flyout.id && Array.isArray(item.children))
    : null;

  useEffect(() => {
    setFlyout(null);
  }, [location.pathname, isExpanded, isMobile]);
  const [expandedGroup, setExpandedGroup] = useState(() => activeGroup);
  const previousPath = useRef(location.pathname);
  const previousUser = useRef(Boolean(user));

  useEffect(() => {
    if (previousPath.current !== location.pathname || (!previousUser.current && user)) {
      previousPath.current = location.pathname;
      setExpandedGroup(getActiveGroup(menuItems, location.pathname));
    }
    previousUser.current = Boolean(user);
  }, [location.pathname, menuItems, user]);

  return (
    <>
      <SidebarSurface
        className={`admin-aside ${
          isMobile ? "offcanvas" : "desktop"
        } ${isExpanded ? "expanded" : "collapsed"} ${
          isLocked ? "locked" : ""
        } ${offcanvasOpen ? "open" : ""}`}
        side="left"
        collapsible={isMobile ? "offcanvas" : "icon"}
        onClick={handleAsideClick}
      >
        <SidebarHeader className="aside-header">
          <div className="logo-icon">
            <img src="/logo512.png" alt={t("AppName")} />
          </div>

          {(isExpanded || isMobile) && (
            <span className="brand-text">{t("portal")}</span>
          )}

        </SidebarHeader>

        <SidebarContent
          className="aside-menu"
          role="navigation"
          aria-label={t("sidebar.navigation", "Main navigation")}
        >
          <SidebarMenu>
            {menuItems.map((item) => (
              <SidebarMenuItem key={item.id ?? item.to}>
                {item.children ? (
                  <>
                    <InstantSidebarTooltip label={item.label} disabled={isExpanded || isMobile}>
                      <button
                        type="button"
                        className={`admin-link sidebar-group-button ${item.id === activeGroup ? "group-active" : ""} ${!isExpanded && !isMobile ? "only-icon" : ""}`}
                        aria-label={item.label}
                        aria-expanded={isExpanded || isMobile ? expandedGroup === item.id : flyout?.id === item.id}
                        aria-haspopup={!isExpanded && !isMobile ? "dialog" : undefined}
                        aria-controls={!isExpanded && !isMobile ? (flyout?.id === item.id ? "sidebar-flyout" : undefined) : `sidebar-group-${item.id}`}
                        onClick={(event) => {
                          if (!isExpanded && !isMobile) {
                            setFlyout({ id: item.id, anchor: event.currentTarget });
                            return;
                          }
                          setExpandedGroup((current) => current === item.id ? null : item.id);
                        }}
                      >
                        <span className="icon">{item.icon}</span>
                        {(isExpanded || isMobile) && (
                          <>
                            <span className="label">{item.label}</span>
                            <ChevronDown className={`group-chevron ${expandedGroup === item.id ? "open" : ""}`} aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </InstantSidebarTooltip>
                    <div
                      id={`sidebar-group-${item.id}`}
                      className="sidebar-submenu"
                      role="list"
                      hidden={expandedGroup !== item.id || (!isExpanded && !isMobile)}
                    >
                      {item.children.map((child) => (
                        <SidebarMenuItem key={child.to}>
                          <NavLink
                            to={child.to}
                            className={({ isActive }) => `admin-link ${isActive ? "active" : ""}`}
                            aria-label={child.label}
                            onClick={handleNavigation}
                          >
                            <span className="icon">{child.icon}</span>
                            <span className="label">{child.label}</span>
                          </NavLink>
                        </SidebarMenuItem>
                      ))}
                    </div>
                  </>
                ) : (
                  <InstantSidebarTooltip label={item.label} disabled={isExpanded || isMobile}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={
                        `admin-link ${matchPath({ path: item.to, end: item.end ?? false }, location.pathname) ? "active" : ""} ${
                          !isExpanded && !isMobile ? "only-icon" : ""
                        }`
                      }
                      aria-label={item.label}
                      onClick={handleNavigation}
                    >
                      <span className="icon">{item.icon}</span>
                      {(isExpanded || isMobile) && (
                        <span className="label">{item.label}</span>
                      )}
                    </NavLink>
                  </InstantSidebarTooltip>
                )}
              </SidebarMenuItem>
            ))}

            {!isMobile && (
              <SidebarMenuItem>
                <InstantSidebarTooltip
                  label={isLocked ? t("sidebar.unlock") : t("sidebar.lock")}
                  disabled={isExpanded}
                >
                  <button
                    type="button"
                    className={`admin-link sidebar-toggle ${!isExpanded ? "only-icon" : ""}`}
                    aria-label={isLocked ? t("sidebar.unlock") : t("sidebar.lock")}
                    aria-pressed={isLocked}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleSidebar();
                    }}
                  >
                    <span className="icon">
                      {isLocked ? <ChevronLeft aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                    </span>
                    {isExpanded && (
                      <span className="label">
                        {isLocked ? t("sidebar.unlock") : t("sidebar.lock")}
                      </span>
                    )}
                  </button>
                </InstantSidebarTooltip>
              </SidebarMenuItem>
            )}
          </SidebarMenu>

          <Separator className="menu-divider" />

          {user && (
            <SidebarMenu>
              <SidebarMenuItem>
                <InstantSidebarTooltip label={t("sidebar.profile")} disabled={isExpanded || isMobile}>
                  <NavLink
                    to="/dashboard/profile"
                    className={`admin-link ${matchPath({ path: "/dashboard/profile", end: false }, location.pathname) ? "active" : ""} ${!isExpanded && !isMobile ? "only-icon" : ""}`}
                    aria-label={t("sidebar.profile")}
                    onClick={handleNavigation}
                  >
                    <span className="icon"><UserRound /></span>
                    {(isExpanded || isMobile) && <span className="label">{t("sidebar.profile")}</span>}
                  </NavLink>
                </InstantSidebarTooltip>
              </SidebarMenuItem>
            </SidebarMenu>
          )}

          {!user && (
            <NavLink
              to="/auth"
              className={`admin-link login-btn ${
                !isExpanded && !isMobile ? "only-icon" : ""
              }`}
              onClick={handleNavigation}
            >
              <span className="icon"><LogIn size={20} /></span>
              {(isExpanded || isMobile) && (
                <span className="label">
                  {t("auth.login")} / {t("auth.signUp")}
                </span>
              )}
            </NavLink>
          )}
        </SidebarContent>

        {user && (
          <SidebarFooter className="aside-actions">
            <InstantSidebarTooltip label={t("sidebar.exit")} disabled={isExpanded || isMobile}>
              <Button
                type="button"
                variant="ghost"
                className={`admin-link btn-reset logout ${
                  !isExpanded && !isMobile ? "only-icon" : ""
                }`}
                aria-label={t("sidebar.exit")}
                onClick={(e) => {
                  e.stopPropagation();
                  logout();
                }}
              >
                <span className="icon"><LogOut size={20} /></span>
                {(isExpanded || isMobile) && (
                  <span className="label">{t("sidebar.exit")}</span>
                )}
              </Button>
            </InstantSidebarTooltip>
          </SidebarFooter>
        )}

      </SidebarSurface>

      <Popover
        open={Boolean(flyoutGroup && !isExpanded && !isMobile)}
        anchorEl={flyout?.anchor}
        onClose={() => setFlyout(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { className: "sidebar-flyout" } }}
      >
        {flyoutGroup && (
          <div id="sidebar-flyout" role="dialog" aria-label={flyoutGroup.label}>
            <div className="sidebar-flyout-title">{flyoutGroup.label}</div>
            <nav aria-label={flyoutGroup.label}>
              {flyoutGroup.children.map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to}
                  className={({ isActive }) => `admin-link ${isActive ? "active" : ""}`}
                  onClick={handleNavigation}
                >
                  <span className="icon">{child.icon}</span>
                  <span className="label">{child.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </Popover>

      {isMobile && offcanvasOpen && (
        <div
          className="aside-overlay"
          onClick={() => setOffcanvasOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
